import { inject, Injectable, signal } from '@angular/core';

import { environment } from '../../environments/environment';
import { loginResponse as LoginResponse } from '../interfaces/apis/login';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, catchError, of } from 'rxjs';

import { Router } from '@angular/router';
import { ConfigService } from './config.service';

type LoginParameters = { email: string; password: string };
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private router = inject(Router);

  // Service Base URLs
  private authBaseUrl: string = `${environment.apiBaseUrl}/auth/`;
  private usersBaseUrl: string = `${environment.apiBaseUrl}/users/`;

  // Authentication States
  private userData = signal<LoginResponse['data']['user'] | null>(null);
  private userPolicy = signal<LoginResponse['data']['userPolicy'] | null>(null);
  private loggedIn = signal<boolean>(false);

  /**
   * Logs a user in given username and password.
   * @param {Object} userCredintials - Users Credintials
   * @param {string} userCredintials.email - Username
   * @param {string} userCredintials.password - Password
   */
  public login = async ({
    email,
    password,
  }: LoginParameters): Promise<LoginResponse['data']> => {
    const response = await lastValueFrom(
      this.http.post<LoginResponse>(this.authBaseUrl + 'users/login', {
        email,
        password,
      }),
    );

    this.userData.set(response.data.user);
    this.userPolicy.set(response.data.userPolicy);
    this.loggedIn.set(true);

    return response.data;
  };

  /**
   * @description Logsout a user.
   */
  public logout = async () => {
    try {
      // Call backend logout endpoint to clear HTTP-only cookies
      await lastValueFrom(this.http.post(this.authBaseUrl + 'logout', {}));
    } catch (error) {
      console.warn('Logout request failed:', error);
    }

    this.configService.resetToDefaultDateRange();

    // Clear authentication data
    this.userData.set(null);
    this.userPolicy.set(null);
    this.loggedIn.set(false);

    // Navigate to login using Angular Router (no hard reload)
    this.router.navigate(['/login']);
  };

  get currentUserPolicy() {
    return this.userPolicy();
  }

  /**
   * Checks if user is authenticated
   * @returns {Promise<boolean>}
   */
  public isLoggedIn = async (): Promise<boolean> => {
    // Return cached state if available

    if (this.loggedIn() && !this.userPolicy()) return false;
    if (this.loggedIn() && this.userPolicy()) return true;
    // Try to fetch user data

    const response = await this.fetchUserData();
    return response !== null;
  };

  /**
   * Gets user data (cached or fresh)
   * @returns {Promise<LoginResponse['data']['user'] | null>}
   */
  public getUserData = async (): Promise<
    LoginResponse['data']['user'] | null
  > => {
    // Return cached data if available
    if (this.userData()) return this.userData();

    // Try to fetch user data
    const response = await this.fetchUserData();
    return response?.data.user || null;
  };

  /**
   * Fetches user data from the server and updates authentication state
   * @returns {Promise<LoginResponse | null>}
   */
  private fetchUserData = async (): Promise<LoginResponse | null> => {
    try {
      const response = await lastValueFrom(
        this.http
          .get<LoginResponse>(this.usersBaseUrl + 'me')
          .pipe(catchError(() => of(null))),
      );

      if (response) {
        this.userData.set(response.data.user);
        this.userPolicy.set(response.data.userPolicy);
        this.loggedIn.set(true);
        return response;
      }
    } catch (error) {
      console.warn('Failed to fetch user data:', error);
    }

    return null;
  };
}
